import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Drawer,
  IconButton,
  Tooltip
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { ArrowLeft, FileText, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import AiChat from '../components/AiChat/AiChat';

export default function NegotiationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [negotiation, setNegotiation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesPage, setNotesPage] = useState(0);
  const [notesRowsPerPage, setNotesRowsPerPage] = useState(10);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksPage, setTasksPage] = useState(0);
  const [tasksRowsPerPage, setTasksRowsPerPage] = useState(10);
  const [taskFilter, setTaskFilter] = useState('all');
  const [aiOpen, setAiOpen] = useState(false);
  const totalTasks = tasks.length;
  const isTaskCompleted = (task) => {
    const value = task && task.eseguito;
    return value === 1 || value === '1';
  };
  const completedTasks = tasks.filter(isTaskCompleted).length;
  const pendingTasks = totalTasks - completedTasks;
  const priorityTasks = tasks.filter(task => task.prioritario).length;
  const filteredTasks = tasks.filter(task => {
    if (taskFilter === 'completed') return isTaskCompleted(task);
    if (taskFilter === 'pending') return !isTaskCompleted(task);
    if (taskFilter === 'priority') return task.prioritario;
    return true;
  });

  const getTaskDotColor = (task) => {
    if (isTaskCompleted(task)) return 'success.main';
    if (!task.data_scadenza) return null;
    const dueDate = new Date(task.data_scadenza);
    if (Number.isNaN(dueDate.getTime())) return null;
    const now = new Date();
    if (dueDate < now) return 'error.main';
    const diffMs = dueDate.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays <= 3) return 'warning.main';
    return null;
  };
  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  useEffect(() => {
    const fetchNegotiation = async () => {
      try {
        const res = await api.get(`/negotiation/${id}`);
        setNegotiation(res.data);
      } catch (error) {
        console.error('Error fetching negotiation details:', error);
        toast.error('Errore nel caricamento dei dettagli Pratica');
      } finally {
        setLoading(false);
      }
    };

    const fetchNotes = async () => {
      setNotesLoading(true);
      try {
        const res = await api.get(`/negotiation/${id}/notes`);
        setNotes(res.data || []);
      } catch (error) {
        console.error('Error fetching negotiation notes:', error);
      } finally {
        setNotesLoading(false);
      }
    };

    const fetchTasks = async () => {
      setTasksLoading(true);
      try {
        const res = await api.get(`/negotiation/${id}/tasks`);
        setTasks(res.data || []);
      } catch (error) {
        console.error('Error fetching negotiation tasks:', error);
      } finally {
        setTasksLoading(false);
      }
    };

    if (id) {
      fetchNegotiation();
      fetchNotes();
      fetchTasks();
    }
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();
    
    // Success (Green)
    if (['completato', 'confermato', 'risolto', 'pagato', 'attivo', 'success', 'regolare', 'closed'].some(k => lower.includes(k))) return 'success';
    
    // Error (Red)
    if (['errore', 'annullato', 'bloccato', 'rifiutato', 'chiuso', 'fallito', 'non_pervenuto', 'cancelled'].some(k => lower.includes(k))) return 'error';
    
    // Warning (Orange)
    if (['in attesa', 'pending', 'da verificare', 'sospeso', 'da contattare', 'richiamare'].some(k => lower.includes(k))) return 'warning';
    
    // Info (Blue)
    if (['nuovo', 'bozza', 'in lavorazione', 'aperto', 'indeterminato', 'created'].some(k => lower.includes(k))) return 'info';
    
    return 'default';
  };

  const formatKey = (key) => {
    if (key === 'issuer_name') return 'Creditore';
    if (key === 'rif_issuer') return 'Rif Creditore';
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper to get formatted value
  const formatValue = (value, key, data) => {
    if (value === null || value === undefined || value === '') return '-';
    
    // Handle specific foreign keys to show names
    if (key === 'rif_issuer' && data.issuer_name) {
      return `${value} (${data.issuer_name})`;
    }
    if (key === 'rif_category' && data.category_name) {
      return `${value} (${data.category_name})`;
    }
    if (key === 'rif_responsabile_contabile' && data.responsabile_contabile_name) {
      return `${value} (${data.responsabile_contabile_name})`;
    }
    if (key === 'rif_responsabile_legale' && data.responsabile_legale_name) {
      return `${value} (${data.responsabile_legale_name})`;
    }
    if (key === 'rif_dossier' && data.dossier_reference) {
      return `${value} (${data.dossier_reference})`;
    }

    if (key.includes('created_at') || key.includes('updated_at') || key.includes('data_') || key.includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    if (typeof value === 'boolean') return value ? 'Sì' : 'No';
    return value;
  };

  // Tab configuration
  const tabConfig = [
    {
      label: 'Dettagli Generali',
      fields: [
        'reference', 'status', 'urgency', 'description_urgency',
        'dossier_reference', 'issuer_name', 'category_name',
        'responsabile_legale_name', 'responsabile_contabile_name',
        'created_at', 'updated_at'
      ]
    },
    {
      label: 'Stato & Date',
      fields: [
        'data_apertura', 'data_chiusura', 'modalita_chiusura', 
        'dettaglio_chiusura', 'importo_chiusura', 'importo_accordato',
        'data_decadenza', 'attiva_decadenza', 'dovuto_decadenza',
        'data_assegnazione_legale'
      ]
    },
    {
      label: 'Finanziamento',
      fields: [
        'fin_numero_contratto', 'fin_data_contratto', 'fin_tipologia',
        'fin_durata_finanziamento', 'fin_importo_rate', 'fin_importo_erogato',
        'fin_importo_da_restituire', 'fin_spese_istruttoria', 'fin_spese_gestione_pratica',
        'fin_premio_assicurazione', 'fin_premio_altre_assicurazioni', 'fin_imposte',
        'fin_rendiconto_annuale', 'fin_altre_spese', 'fin_tan', 'fin_taeg',
        'fin_taeg_assicurazione', 'fin_tae', 'fin_tasso_mora',
        'fin_spese_solleciti_telefonici', 'fin_spese_recupero_crediti',
        'fin_spese_risoluzioni_contratto', 'fin_penale_ritardato_pagamento',
        'fin_penale_decadenza_beneficio', 'fin_perizia_citta_contratto',
        'fin_perizia_utente', 'fin_perizia_fidejussore', 'fin_perizia_societa_cessione',
        'fin_perizia_giorno_paga', 'fin_perizia_rimborso_estinzione', 'fin_perizia_tegm',
        'fin_perizia_tasso_soglia', 'fin_perizia_calcolo', 'fin_perizia_data_contratto',
        'fin_perizia_importo_prestito', 'fin_perizia_descrizione_rate'
      ]
    },
    {
      label: 'Mutuo',
      fields: [
        'mutuo_numero_contratto', 'mutuo_data_contratto', 'mutuo_tipologia',
        'mutuo_durata_finanziamento', 'mutuo_importo_rate', 'mutuo_importo_erogato',
        'mutuo_importo_da_restituire', 'mutuo_spese_istruttoria', 'mutuo_spese_gestione_pratica',
        'mutuo_premio_assicurazione', 'mutuo_premio_altre_assicurazioni', 'mutuo_imposte',
        'mutuo_rendiconto_annuale', 'mutuo_altre_spese', 'mutuo_tan', 'mutuo_taeg',
        'mutuo_taeg_assicurazione', 'mutuo_tae', 'mutuo_tasso_mora',
        'mutuo_spese_solleciti_telefonici', 'mutuo_spese_recupero_crediti',
        'mutuo_spese_risoluzioni_contratto', 'mutuo_penale_ritardato_pagamento',
        'mutuo_penale_decadenza_beneficio', 'mutuo_valore_immobile', 'mutuo_vendita'
      ]
    },
    {
      label: 'Carta & Conto',
      fields: [
        'carta_numero', 'carta_fido', 'carta_tan', 'carta_taeg', 'carta_linea_credito',
        'conto_numero', 'conto_fido', 'conto_tan', 'conto_taeg'
      ]
    },
    {
      label: 'Note & Descrizione',
      fields: ['description', 'note']
    },
    {
      label: 'Task & Promemoria',
      fields: []
    },
    {
      label: 'Sistema',
      fields: [
        'id', 'rif_hubspot', 'soft_delete', 
        'rif_dossier', 'rif_issuer', 'rif_category', 
        'rif_installment_group', 'rif_responsabile_contabile', 'rif_responsabile_legale'
      ]
    }
  ];

  // Helper to get fields for a specific tab
  const getFieldsForTab = (tabIndex) => {
    if (!negotiation) return [];
    const config = tabConfig[tabIndex];
    if (!config) return []; 
    
    return Object.keys(negotiation).filter(key => 
      config.fields.includes(key.toLowerCase()) || 
      config.fields.some(field => key.toLowerCase() === field)
    );
  };

  // Helper to get remaining fields for "Altro"
  const getOtherFields = () => {
    if (!negotiation) return [];
    const allConfiguredFields = tabConfig.flatMap(t => t.fields);
    return Object.keys(negotiation).filter(key => {
      const lowerKey = key.toLowerCase();
      return !allConfiguredFields.some(field => lowerKey === field);
    });
  };

  // Calculate visible tabs based on available data
  const visibleTabs = [
    ...tabConfig.map((tab, index) => ({
      ...tab,
      originalIndex: index,
      hasData: tab.label === 'Note & Descrizione'
        ? getFieldsForTab(index).length > 0 || notes.length > 0
        : tab.label === 'Task & Promemoria'
        ? true
        : getFieldsForTab(index).length > 0
    })),
    {
      label: 'Altro',
      originalIndex: -1, // Special index for Altro
      hasData: getOtherFields().length > 0
    }
  ].filter(tab => tab.hasData);

  const currentTab = visibleTabs[tabValue];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!negotiation) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6">Negoziazione non trovata</Typography>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Torna indietro
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowLeft />}
            onClick={() => navigate('/negotiation')}
            variant="outlined"
          >
            Torna alla lista
          </Button>
          <Typography variant="h4" component="h1">
            Dettagli Pratica: {negotiation.reference}
          </Typography>
        </Box>
        <Tooltip title="Apri assistente AI per questa pratica">
          <IconButton
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
            onClick={() => setAiOpen(true)}
            size="large"
            aria-label="Assistente AI pratica"
          >
            <Sparkles size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ mb: 3, p: 3, boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', borderRadius: 4 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <FileText size={32} color="#637381" />
              </Grid>
              <Grid item xs>
                <Typography variant="h6">
                  {negotiation.reference || 'Nessun riferimento'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip 
                    label={negotiation.status || 'Status sconosciuto'} 
                    color={getStatusColor(negotiation.status)} 
                    size="small" 
                    variant="filled"
                  />
                  {negotiation.urgency && (
                    <Chip 
                      label={`Urgenza: ${negotiation.urgency}`} 
                      color={negotiation.urgency === 'high' ? 'error' : 'default'} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)', borderRadius: 4 }}>
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 500 }}>
              <Tabs 
                orientation="vertical"
                variant="scrollable"
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="pratica details tabs"
                sx={{ borderRight: 1, borderColor: 'divider', minWidth: 160, flexShrink: 0 }}
              >
                {visibleTabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} sx={{ alignItems: 'flex-start', textAlign: 'left' }} />
                ))}
              </Tabs>

              <Box sx={{ p: 3, flexGrow: 1, width: '100%' }}>
            {currentTab && currentTab.label === 'Note & Descrizione' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={3}>
                  {getFieldsForTab(currentTab.originalIndex).map(key => (
                    <Grid item xs={12} md={6} key={key}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {formatKey(key)}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {formatValue(negotiation[key], key, negotiation)}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Note collegate alla Pratica
                  </Typography>
                  {notesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nessuna nota presente per questa pratica.
                    </Typography>
                  ) : (
                    <Box>
                      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Utente</TableCell>
                              <TableCell>Importante</TableCell>
                              <TableCell>Appunta</TableCell>
                              <TableCell>Descrizione</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {notes
                              .slice(notesPage * notesRowsPerPage, notesPage * notesRowsPerPage + notesRowsPerPage)
                              .map((note) => (
                              <TableRow key={note.id} hover>
                                <TableCell>
                                  {note.created_at ? new Date(note.created_at).toLocaleString() : '-'}
                                </TableCell>
                                <TableCell>
                                  {[note.user_name, note.user_surname].filter(Boolean).join(' ') || `ID: ${note.rif_utente}`}
                                </TableCell>
                                <TableCell>
                                  {note.importante ? (
                                    <Chip label="Sì" color="warning" size="small" />
                                  ) : (
                                    <Chip label="No" variant="outlined" size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {note.appunta ? (
                                    <Chip label="Sì" color="info" size="small" />
                                  ) : (
                                    <Chip label="No" variant="outlined" size="small" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {stripHtml(note.descrizione) || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={notes.length}
                        rowsPerPage={notesRowsPerPage}
                        page={notesPage}
                        onPageChange={(e, newPage) => setNotesPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setNotesRowsPerPage(parseInt(e.target.value, 10));
                          setNotesPage(0);
                        }}
                        labelRowsPerPage="Righe per pagina:"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            ) : currentTab && currentTab.label === 'Task & Promemoria' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Task & Promemoria collegati alla Pratica
                </Typography>
                {tasksLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                      <Chip
                        label={`Totale: ${totalTasks}`}
                        variant={taskFilter === 'all' ? 'filled' : 'outlined'}
                        color="default"
                        size="small"
                        onClick={() => {
                          setTaskFilter('all');
                          setTasksPage(0);
                        }}
                      />
                      <Chip
                        label={`Completati: ${completedTasks}`}
                        variant={taskFilter === 'completed' ? 'filled' : 'outlined'}
                        color="success"
                        size="small"
                        onClick={() => {
                          setTaskFilter('completed');
                          setTasksPage(0);
                        }}
                      />
                      <Chip
                        label={`Da fare: ${pendingTasks}`}
                        variant={taskFilter === 'pending' ? 'filled' : 'outlined'}
                        color="warning"
                        size="small"
                        onClick={() => {
                          setTaskFilter('pending');
                          setTasksPage(0);
                        }}
                      />
                      <Chip
                        label={`Prioritari: ${priorityTasks}`}
                        variant={taskFilter === 'priority' ? 'filled' : 'outlined'}
                        color="error"
                        size="small"
                        onClick={() => {
                          setTaskFilter('priority');
                          setTasksPage(0);
                        }}
                      />
                    </Box>
                    {!tasks.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Nessun task presente per questa pratica.
                      </Typography>
                    ) : filteredTasks.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Nessun task per questo filtro.
                      </Typography>
                    ) : (
                      <Box>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Scadenza</TableCell>
                                <TableCell>Mittente</TableCell>
                                <TableCell>Destinatario</TableCell>
                                <TableCell>Prioritario</TableCell>
                                <TableCell>Eseguito</TableCell>
                                <TableCell>Notifica</TableCell>
                                <TableCell>Titolo</TableCell>
                                <TableCell>Descrizione</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredTasks
                                .slice(tasksPage * tasksRowsPerPage, tasksPage * tasksRowsPerPage + tasksRowsPerPage)
                                .map((task) => {
                                  const dotColor = getTaskDotColor(task);
                                  let overdueDays = null;
                                  if (!isTaskCompleted(task) && task.data_scadenza) {
                                    const dueDate = new Date(task.data_scadenza);
                                    if (!Number.isNaN(dueDate.getTime()) && dueDate < new Date()) {
                                      const diffMs = new Date().getTime() - dueDate.getTime();
                                      const days = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
                                      overdueDays = days;
                                    }
                                  }
                                  return (
                                <TableRow key={task.id} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {dotColor && (
                                          <Box
                                            sx={{
                                              width: 10,
                                              height: 10,
                                              borderRadius: '50%',
                                              bgcolor: dotColor
                                            }}
                                          />
                                        )}
                                        {task.data_scadenza ? new Date(task.data_scadenza).toLocaleString() : '-'}
                                      </Box>
                                      {overdueDays !== null && (
                                        <Typography variant="caption" color="error.main">
                                          Scaduto da {overdueDays} giorni – verrà inoltrato alert all&apos;amministratore
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {[task.mittente_name, task.mittente_surname].filter(Boolean).join(' ') || (task.rif_mittente ? `ID: ${task.rif_mittente}` : '-')}
                                  </TableCell>
                                  <TableCell>
                                    {[task.destinatario_name, task.destinatario_surname].filter(Boolean).join(' ') || (task.rif_utente ? `ID: ${task.rif_utente}` : '-')}
                                  </TableCell>
                                  <TableCell>
                                    {task.prioritario ? (
                                      <Chip label="Sì" color="error" size="small" />
                                    ) : (
                                      <Chip label="No" variant="outlined" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isTaskCompleted(task) ? (
                                      <Chip label="Completato" color="success" size="small" />
                                    ) : (
                                      <Chip label="Da fare" variant="outlined" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {task.enable_notifica ? (
                                      <Chip label="On" color="info" size="small" />
                                    ) : (
                                      <Chip label="Off" variant="outlined" size="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {task.titolo || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {stripHtml(task.descrizione) || '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          rowsPerPageOptions={[10, 25, 50]}
                          component="div"
                          count={filteredTasks.length}
                          rowsPerPage={tasksRowsPerPage}
                          page={tasksPage}
                          onPageChange={(e, newPage) => setTasksPage(newPage)}
                          onRowsPerPageChange={(e) => {
                            setTasksRowsPerPage(parseInt(e.target.value, 10));
                            setTasksPage(0);
                          }}
                          labelRowsPerPage="Righe per pagina:"
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : currentTab && (
              <Grid container spacing={3}>
                {(currentTab.originalIndex === -1 
                  ? getOtherFields() 
                  : getFieldsForTab(currentTab.originalIndex)
                ).map(key => (
                  <Grid item xs={12} md={6} key={key}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {formatKey(key)}
                    </Typography>
                    {['status'].includes(key) ? (
                      <Chip 
                        label={negotiation[key]} 
                        color={getStatusColor(negotiation[key])} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontWeight: 'bold', mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: key === 'id' ? 'medium' : 'regular' }}>
                        {formatValue(negotiation[key], key, negotiation)}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            p: 2,
            boxSizing: 'border-box'
          }
        }}
      >
        <AiChat
          title="Assistente AI – Pratica"
          subtitle="Chat contestuale basata su questa pratica e note collegate."
          context={{ negotiationId: Number(id) }}
        />
      </Drawer>
    </Container>
  );
}
